import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

export function formatViews(views: number) {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

interface MovieCardProps {
  movie: Movie;
  variant?: 'grid' | 'list';
}

export default function MovieCard({ movie, variant = 'grid' }: MovieCardProps) {
  if (variant === 'list') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-[120px] h-[180px] sm:h-[170px] shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={movie.cover}
            alt={`${movie.title} 海报`}
            title={`${movie.title} 韩国伦理影视`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
              {movie.title}
            </h3>
            <span className="shrink-0 text-sm font-semibold text-accent-600 whitespace-nowrap">
              {movie.rating.toFixed(1)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-foreground-500">
            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{movie.categoryLabel}</span>
            <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">{movie.genre}</span>
            <span>{movie.year}</span>
          </div>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-2">{movie.summary}</p>
          <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {formatViews(movie.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-star-line" /> {movie.cast[0]} 主演
            </span>
            <span className="ml-auto text-accent-600">{movie.status}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
        <img
          src={movie.cover}
          alt={`${movie.title} 海报`}
          title={`${movie.title} 韩国伦理影视`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[11px] bg-accent-500 text-background-50">
          {movie.genre}
        </span>
        <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md text-[11px] bg-primary-950/70 text-background-50 backdrop-blur">
          {movie.year}
        </span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
            {movie.title}
          </h3>
          <span className="shrink-0 text-sm font-semibold text-accent-600">{movie.rating.toFixed(1)}</span>
        </div>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{movie.summary}</p>
        <div className="mt-auto pt-3 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" /> {formatViews(movie.views)}
          </span>
          <span className="text-accent-600">{movie.status}</span>
        </div>
      </div>
    </Link>
  );
}